import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { notificationService } from '../services/notificationService';
import { useNavigate } from 'react-router-dom';
import { Bell, Heart, MessageCircle, AtSign, ArrowLeft, CheckCircle2, Circle, X, Flame } from 'lucide-react';
import AvatarRenderer from '../components/AvatarRenderer';
import { AppBackButton } from '../components/AppBackButton';
import { AnimatedIcon } from '../components/AnimatedIcon';

const NotificationsPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!user) return;
        const load = async () => {
            try {
                const notifs = await notificationService.getNotifications(user.id);
                setNotifications(notifs);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [user]);

    const handleRead = async (notif: any) => {
        if (!user) return;
        if (!notif.is_read) {
            await notificationService.markAsRead(notif.id, user.id);
            setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: 1 } : n));
        }

        // Navigate based on target
        if (notif.target_type === 'post' || notif.target_type === 'comment') {
            navigate(`/community-feed/${notif.target_id}`);
        } else if (notif.target_type === 'club_comment') {
            navigate(`/book-club`);
        } else if (notif.type === 'streak' || notif.target_type === 'streak_milestone') {
            navigate('/friends');
        }
    };

    const handleMarkAllRead = async () => {
        if (!user) return;
        await notificationService.markAllAsRead(user.id);
        setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
    };

    const getIcon = (type: string) => {
        switch(type) {
            case 'like': return <Heart className="text-bad fill-bad" size={16} />;
            case 'comment': return <MessageCircle className="text-tintd-sky" size={16} />;
            case 'reply': return <MessageCircle className="text-ok" size={16} />;
            case 'mention': return <AtSign className="text-warn" size={16} />;
            case 'streak': return <Flame className="text-warn fill-warn" size={16} />;
            default: return <Bell className="text-muted" size={16} />;
        }
    };

    const getText = (notif: any, actorName: string) => {
        switch(notif.type) {
            case 'like': return <><span className="font-bold">{actorName}</span> {t('notifications.likedPost')}</>;
            case 'comment': return <><span className="font-bold">{actorName}</span> {t('notifications.commentedPost')}</>;
            case 'reply': return <><span className="font-bold">{actorName}</span> {t('notifications.repliedComment')}</>;
            case 'mention': return <><span className="font-bold">{actorName}</span> {t('notifications.mentionedYou')}</>;
            case 'streak': return <><span className="font-bold">{actorName}</span> atingiu {notif.target_id} dias de streak! 🎉🥳</>;
            default: return <><span className="font-bold">{actorName}</span> {t('notifications.interactedWithYou')}</>;
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col h-full bg-transparent p-4 md:p-8 max-w-3xl mx-auto w-full pb-32">
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <AppBackButton />
                        <h1 className="font-display text-[34px] md:text-5xl font-light tracking-[-0.045em] leading-none text-ink">{t('notifications.title')}<span className="text-primary">.</span></h1>
                    </div>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center">
                    <div className="animate-spin text-primary mb-4"><Bell size={40} /></div>
                    <p className="text-muted font-medium">{t('notifications.fetching')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-transparent p-4 md:p-8 max-w-3xl mx-auto w-full pb-32">
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                    <AppBackButton />
                    <h1 className="font-display text-[34px] md:text-5xl font-light tracking-[-0.045em] leading-none text-ink">{t('notifications.title')}<span className="text-primary">.</span></h1>
                </div>
                <div className="flex items-center gap-2 md:gap-4">
                    {notifications.some(n => !n.is_read) && (
                        <button onClick={handleMarkAllRead} className="text-xs font-bold text-primary bg-primary/10 px-4 py-2 rounded-full hover:bg-primary/20 transition-colors">
                            {t('notifications.markAllRead')}
                        </button>
                    )}
                </div>
            </div>

            {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                    <div className="w-24 h-24 bg-tint-stone/60 rounded-full flex items-center justify-center text-muted/60 mb-6">
                        <AnimatedIcon motion="swing" active><Bell size={36} strokeWidth={1.5} /></AnimatedIcon>
                    </div>
                    <h3 className="text-lg font-bold text-muted">{t('notifications.none')}</h3>
                    <p className="text-sm text-muted mt-2">{t('notifications.noneDesc')}</p>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {notifications.map(n => (
                        <div 
                            key={n.id} 
                            onClick={() => handleRead(n)}
                            className={`flex items-start gap-4 p-4 md:p-5 rounded-2xl md:rounded-[2rem] border transition-all cursor-pointer hover:scale-[1.01] ${
                                !n.is_read 
                                ? 'bg-surface border-primary/80' 
                                : 'bg-tint-stone/40 border-black/[0.06] dark:border-white/[0.06] opacity-80'
                            }`}
                        >
                            <div className="relative shrink-0 w-12 h-12">
                                {(() => {
                                    const rawConfig = n.actor?.avatar_config;
                                    const parsedConfig = typeof rawConfig === 'string' ? JSON.parse(rawConfig) : rawConfig;
                                    const isValidConfig = parsedConfig && parsedConfig.body;
                                    const displayName = n.actor?.display_name || n.actor?.username || 'U';

                                    if (isValidConfig) {
                                        return (
                                            <div className="w-12 h-12 rounded-full border border-black/[0.06] dark:border-white/[0.06] overflow-hidden bg-tint-stone/40">
                                                <AvatarRenderer config={parsedConfig} />
                                            </div>
                                        );
                                    }

                                    return (
                                        <img
                                            src={n.actor?.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${displayName}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`}
                                            className="w-12 h-12 rounded-full border border-black/[0.06] dark:border-white/[0.06] object-cover bg-tint-stone/40"
                                            alt={displayName}
                                            onError={e => { (e.target as HTMLImageElement).src = `https://api.dicebear.com/7.x/initials/svg?seed=${displayName}&backgroundColor=b0805f,7d9278,8a85a8,b39a64,6f93a6&textColor=fbf8f3`; }}
                                        />
                                    );
                                })()}
                                <div className="absolute -bottom-1 -right-1 bg-surface rounded-full p-1 z-10">
                                    {getIcon(n.type)}
                                </div>
                            </div>
                            
                            <div className="flex-1 min-w-0 flex flex-col justify-center py-1">
                                <p className="text-sm text-ink leading-tight">
                                    {getText(n, n.actor?.display_name || n.actor?.username || 'Alguém')}
                                </p>
                                <span className="text-[10px] font-normal text-muted mt-1 uppercase font-mono tracking-[0.14em]">
                                    {new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(n.created_at))}
                                </span>
                            </div>

                            <div className="shrink-0 flex items-center justify-center h-full pt-3 pl-2">
                                {!n.is_read ? (
                                    <Circle className="text-primary fill-primary" size={10} />
                                ) : (
                                    <CheckCircle2 className="text-muted/60" size={16} />
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default NotificationsPage;
